/**
Author - Sahil Dhingra
Git - https://github.com/sahildhingra-221
**/

import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import {colors} from "nxtconstants";
import {WealthyEnv} from "helpers";

import {
  SectionTitle,
  container
} from "../common";
import {GetStartedModal} from "components/commons";

const arrowRightIcon = `${WealthyEnv.BROKING_CDN_URL}/arrow-right-blue.png`;
const candlesBg = `${WealthyEnv.BROKING_CDN_URL}/candles-bg.png`;
const ruppeeSymbol = `${WealthyEnv.BROKING_CDN_URL}/ruppee-symbol.png`;

const propTypes = {
  partnerData: PropTypes.object,
  rCode: PropTypes.string,
  setToggleModal: PropTypes.func,
  toggleModal: PropTypes.bool
};

const BrokeragePricing = ({
  brokerageList,
  rCode,
  partnerData,
  setToggleModal,
  toggleModal
}) => {
  return (
    <>
      <BrokerageSection className={partnerData?.name ? "proposal-pricing" : ""}>
        <SectionHeadingWrapper>
          <SectionTitle hasTag>Unbeatable brokerage charges
          </SectionTitle>
        </SectionHeadingWrapper>
        <BrokerageCardsWrapper>
          {
            brokerageList.map((item, index) => (
              <CardItem key={index}>
                <PriceSection>
                  <Price>{item.price}</Price>
                </PriceSection>
                <AboutPrice>
                  {item.title}
                </AboutPrice>
                <PriceContext>
                  {item?.context}
                </PriceContext>
              </CardItem>
            ))
          }
        </BrokerageCardsWrapper>
        {
          partnerData?.name ? (
            <GetStartedLinkWrapper>
              <GetStartedCta onClick={() => setToggleModal(!toggleModal)}>
                Get Started
              </GetStartedCta>
              <PricingLink href={`${WealthyEnv.URL}/pricing`}>View Pricing In Detail</PricingLink>
            </GetStartedLinkWrapper>
          ) : (
            <PricingLink href={`${WealthyEnv.URL}/pricing`}>View Pricing In Detail</PricingLink>
          )
        }
        <BorderLine />
      </BrokerageSection>
      <GetStartedModal
        rCode={rCode}
        setToggleModal={setToggleModal}
        toggleModal={toggleModal}
      />
    </>
  );
};

const BrokerageSection = styled(container)`
  margin: 10rem auto 8rem;
  position: relative;

  &.proposal-pricing {
    margin-top: 5rem;
  }

  @media (max-width: 768px) {
    margin: 3.3rem auto;
    padding-bottom: 3.2rem;

    &.proposal-pricing {
      margin-top: 3.3rem;
    }
  }
`;

const SectionHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GetStartedLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding-top: 6rem;

  a {
    margin: 0;
    border: 0;
    position: relative;
    inset: 0;
  }

  @media (max-width: 768px) {
    padding-top: 0;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1.3rem;
  }
`;

const GetStartedCta = styled.button`
  font-size: 1.8rem;
  background-color: ${colors.primary};
  color: ${colors.white};
  padding: 1.7rem 4.4rem;
  display: inline-block;
  border-radius: 100px;
  transition: all 0.15s ease-in-out;
  width: 300px;
  text-align: center;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  font-weight: 400;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    max-width: 280px;
    width: 180px;
    padding: 1.4rem 3.5rem;
  }
`;

const PricingLink = styled.a`
  display: block;
  color: ${colors.primary};
  border-radius: 100px;
  border: 1px solid #DDD;
  width: fit-content;
  margin: 0 auto;
  margin-top: 6rem;
  padding: 0 1.5rem;
  position: relative;
  padding-right: calc(10px + 2rem);
  font-size: 2.2rem;
  transition: all 0.15s ease-in-out;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    right: 1.5rem;
    height: 1rem;
    width: 1rem;
    transform: translateY(-50%);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${arrowRightIcon});
  }

  &:hover{
    background-color: rgb(103, 37, 244, 0.04);
  }

  @media (max-width: 768px) {
    position: absolute;
    bottom: 3.2rem;
    font-size: 1.6rem;
    left: 0;
    right: 0;
    margin: auto;
  }
`;

const BrokerageCardsWrapper = styled.div`
  display: flex;
  column-gap: 6.4rem;
  padding-top: 4rem;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  @media (max-width: 1200px) {
    column-gap: 3.4rem;
  }

  @media (max-width: 768px) {
    white-space: pre;
    overflow-x: scroll;
    column-gap: 1.6rem;
    padding: 2.4rem 0 3.4rem;
  }

  &::-webkit-scrollbar {
    width: 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const CardItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 7rem 3.6rem 2.6rem;
  border-radius: 24px;
  border: 1px solid #DDD;
  background: #FFF;
  box-shadow: 10px 10px 40px 0px #F6F6F6;

  @media (max-width: 768px) {
    padding: 3.3rem 1.9rem 2rem;
    box-shadow: 4.9px 4.9px 19.6px 0px #F6F6F6;
    min-width: 130px;
  }
`;

const PriceSection = styled.div`
  text-align: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${candlesBg});
  }
`;

const Price = styled.p`
  font-size: 16rem;
  color: ${colors.primary};
  position: relative;
  line-height: 0.5;
  width: fit-content;
  margin: 0 auto;
  letter-spacing: -15px;

  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: -15px;
    height: 50px;
    width: 23px;
    scale: 0.6;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${ruppeeSymbol});
  }

  @media (max-width: 768px) {
    font-size: 7.8rem;
    letter-spacing: -10px;

    &::before {
      height: 25px;
      width: 11.5px;
      top: -5px;
      left: -10px;
    }
  }
`;

const AboutPrice = styled.h3`
  padding-top: 5.2rem;
  text-align: center;
  font-size: 2rem;
  color: ${colors.textGray};

  @media (max-width: 768px) {
    padding-top: 1.9rem;
    font-size: 1.2rem;
    white-space: break-spaces;
  }
`;

const PriceContext = styled.span`
  padding-top: 1.6rem;
  text-align: center;
  font-size: 1.6rem;
  color: ${colors.textGray};
  display: block;
  margin-top: auto;

  @media (max-width: 576px) {
    padding-top: 0.6rem;
    font-size: 1rem;
    white-space: break-spaces;
  }
`;

const BorderLine = styled.div`
  background-image: repeating-linear-gradient(-2deg, #e3e3e3, #e3e3e3 5px, transparent 5px, transparent 20px, #e3e3e3 18px), repeating-linear-gradient(88deg, #e3e3e3, #e3e3e3 5px, transparent 5px, transparent 20px, #e3e3e3 18px), repeating-linear-gradient(178deg, #e3e3e3, #e3e3e3 5px, transparent 5px, transparent 20px, #e3e3e3 18px), repeating-linear-gradient(268deg, #e3e3e3, #e3e3e3 5px, transparent 5px, transparent 20px, #e3e3e3 18px);
  background-size: 1px 100%, 100% 1px, 1px 100% , 100% 1px;
  background-position: 0 0, 0 0, 100% 0, 0 100%;
  background-repeat: no-repeat;
  height: 1px;
  position: absolute;
  bottom: 0;
  left: 2rem;
  right: 2rem;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

BrokeragePricing.propTypes = propTypes;

export default BrokeragePricing;
